import React, { useState } from 'react';

interface PricingTier {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  limitations?: string[];
  recommended?: boolean;
  cta: string;
  savings?: string;
}

export const PricingCTA: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedTier, setSelectedTier] = useState<string>('creator');

  const pricingTiers: PricingTier[] = [
    {
      id: 'free',
      name: 'Free Trial',
      price: '$0',
      period: 'for 7 days',
      description: 'Perfect for testing AutoReelr with your content',
      features: [
        '3 complete reels',
        'All AI features included',
        'Professional voiceovers',
        'Custom thumbnails',
        'HD export quality',
        'No watermarks'
      ],
      limitations: [
        'Limited to 3 reels',
        'No commercial license'
      ],
      cta: '🎬 Start Free Trial',
      savings: 'No commitment required'
    },
    {
      id: 'creator',
      name: 'Creator Pro',
      price: billingPeriod === 'monthly' ? '$29' : '$19',
      period: billingPeriod === 'monthly' ? '/month' : '/month (billed yearly)',
      description: 'For individual creators and small channels',
      features: [
        '50 reels per month',
        'All AI models access',
        '12+ premium voices',
        'Brand color customization',
        'Commercial license',
        'Priority support',
        'Advanced analytics',
        'Custom templates'
      ],
      recommended: true,
      cta: '🚀 Choose Creator Pro',
      savings: billingPeriod === 'yearly' ? 'Save $120/year' : undefined
    },
    {
      id: 'business',
      name: 'Business',
      price: billingPeriod === 'monthly' ? '$79' : '$59',
      period: billingPeriod === 'monthly' ? '/month' : '/month (billed yearly)',
      description: 'For agencies and growing businesses',
      features: [
        '200 reels per month',
        'Team collaboration',
        'White-label options',
        'API access',
        'Custom voice cloning',
        'Advanced brand controls',
        'Dedicated account manager',
        'Custom integrations'
      ],
      cta: '💼 Choose Business',
      savings: billingPeriod === 'yearly' ? 'Save $240/year' : undefined
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      period: 'pricing',
      description: 'For large organizations with custom needs',
      features: [
        'Unlimited reels',
        'Private AI model training',
        'On-premise deployment',
        'Advanced security',
        'Custom workflows',
        'SLA guarantees',
        '24/7 phone support',
        'Success manager'
      ],
      cta: '📞 Contact Sales',
      savings: 'Volume discounts available'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Content Creator',
      company: '@sarahcreates (127k followers)',
      quote: 'AutoReelr increased my posting frequency by 400% while maintaining quality. The ROI is incredible.',
      avatar: 'SC'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Marketing Director',
      company: 'TechFlow Agency',
      quote: 'We\'ve reduced our content production costs by 85% while scaling to 10x more clients.',
      avatar: 'MR'
    },
    {
      name: 'Emily Johnson',
      role: 'Course Creator',
      company: 'SkillBoost Academy',
      quote: 'The time I save with AutoReelr lets me focus on creating actual course content. Game changer.',
      avatar: 'EJ'
    }
  ];

  const faqs = [
    {
      question: 'How does the free trial work?',
      answer: 'Get full access to all features for 7 days with 3 complete reel generations. No credit card required. If you love it, upgrade anytime.'
    },
    {
      question: 'Can I use AutoReelr content commercially?',
      answer: 'Yes! All paid plans include commercial licensing. You own the content you create and can monetize it freely.'
    },
    {
      question: 'What if I need more reels than my plan allows?',
      answer: 'You can upgrade anytime or purchase additional reel credits. We also offer custom enterprise solutions for high-volume needs.'
    },
    {
      question: 'How long does it take to generate a reel?',
      answer: 'Most reels are generated in 3-5 minutes. Cached content (similar to previous generations) can be ready in under 30 seconds.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Absolutely. No long-term contracts. Cancel anytime from your dashboard and keep access until your billing period ends.'
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Main Pricing Section */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Start creating professional reels today. No hidden fees, no surprises. 
              Cancel anytime.
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex bg-white rounded-lg p-1 shadow-md">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-md font-semibold transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-md font-semibold transition-all relative ${
                  billingPeriod === 'yearly'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                  Save 35%
                </span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingTiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-2xl ${
                  tier.recommended 
                    ? 'ring-2 ring-blue-500 scale-105' 
                    : selectedTier === tier.id 
                      ? 'ring-2 ring-blue-300' 
                      : ''
                }`}
                onClick={() => setSelectedTier(tier.id)}
              >
                {tier.recommended && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-center py-2 font-semibold text-sm">
                    ⭐ Most Popular
                  </div>
                )}
                
                <div className={`p-8 ${tier.recommended ? 'pt-14' : ''}`}>
                  {/* Header */}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                    <div className="text-4xl font-bold text-gray-900 mb-1">
                      {tier.price}
                      <span className="text-lg text-gray-600 font-normal">{tier.period}</span>
                    </div>
                    {tier.savings && (
                      <div className="text-green-600 text-sm font-semibold">{tier.savings}</div>
                    )}
                    <p className="text-gray-600 text-sm mt-2">{tier.description}</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-500 mr-3 mt-1">✓</span>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                    {tier.limitations?.map((limitation, index) => (
                      <li key={`limit-${index}`} className="flex items-start text-gray-500">
                        <span className="text-gray-400 mr-3 mt-1">•</span>
                        <span className="text-sm">{limitation}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-all ${
                      tier.recommended
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                        : tier.id === 'free'
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {tier.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Indicators */}
          <div className="mt-16 text-center">
            <p className="text-gray-600 mb-6">Trusted by 10,000+ creators worldwide</p>
            <div className="flex justify-center items-center space-x-8 text-gray-400">
              <div className="flex items-center">
                <span className="text-green-500 mr-2">🔒</span>
                <span>256-bit SSL Encryption</span>
              </div>
              <div className="flex items-center">
                <span className="text-blue-500 mr-2">💳</span>
                <span>Secure Payment Processing</span>
              </div>
              <div className="flex items-center">
                <span className="text-purple-500 mr-2">🛡️</span>
                <span>99.9% Uptime SLA</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            What Our Creators Say
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-sm text-blue-600">{testimonial.company}</div>
                  </div>
                </div>
                <p className="text-gray-700 italic">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h3>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-blue-900 to-purple-900 py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Content Creation?
          </h3>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of creators who've already 10x'd their content output with AutoReelr
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-8 py-4 rounded-lg font-bold text-lg hover:from-yellow-300 hover:to-orange-400 transition-all">
              🎬 Start Free Trial (No Credit Card)
            </button>
            <button className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/30 transition-all border border-white/30">
              📞 Schedule Live Demo
            </button>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            7-day free trial • No commitment • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingCTA;